// openSSE — one EventSource wrapper for every raw SSE consumer.
//
// Before this, five streams (quotes, notifications, position, portfolio-pnl,
// scanner) each did `new EventSource(...)` with either no onerror at all or a
// no-op one, and none had a silent-stream watchdog: a connection that stays
// open but stops delivering (a proxy that dropped the upstream, a backend loop
// that only yields on new data) looked "connected" forever. This mirrors the
// heartbeat + reconnect shape lib/liveFloorConnection.js already uses for the
// floor chat, as a plain function for non-hook call sites.
//
//   const conn = openSSE(`${API}/stream/quotes`, {
//     quotes: (data) => {...},           // named events, JSON-parsed when possible
//   }, { staleMs: 45000, onStatus: (connected) => {...} });
//   ...
//   conn.close();
//
// Every event — including the backend's `ping` — resets the watchdog. When
// nothing arrives for `staleMs`, or the browser reports an error, the source
// is closed and reopened after a short backoff (bounded, with jitter).

const DEFAULT_STALE_MS = 45000;
const MIN_BACKOFF_MS = 1000;
const MAX_BACKOFF_MS = 15000;

export function openSSE(url, handlers = {}, opts = {}) {
  const staleMs = opts.staleMs || DEFAULT_STALE_MS;
  const onStatus = typeof opts.onStatus === "function" ? opts.onStatus : () => {};
  const withCredentials = opts.withCredentials !== false;

  let es = null;
  let closed = false;
  let watchdog = null;
  let reconnectTimer = null;
  let backoff = MIN_BACKOFF_MS;

  const parse = (raw) => {
    try { return JSON.parse(raw); } catch { return raw; }
  };

  const clearTimers = () => {
    if (watchdog) { clearTimeout(watchdog); watchdog = null; }
    if (reconnectTimer) { clearTimeout(reconnectTimer); reconnectTimer = null; }
  };

  const scheduleReconnect = () => {
    if (closed) return;
    clearTimers();
    try { es && es.close(); } catch {}
    es = null;
    onStatus(false);
    const jitter = Math.floor(Math.random() * 400);
    reconnectTimer = setTimeout(connect, backoff + jitter);
    backoff = Math.min(MAX_BACKOFF_MS, backoff * 2);
  };

  const armWatchdog = () => {
    if (watchdog) clearTimeout(watchdog);
    watchdog = setTimeout(scheduleReconnect, staleMs);
  };

  const connect = () => {
    if (closed) return;
    try {
      es = new window.EventSource(url, { withCredentials });
    } catch {
      scheduleReconnect();
      return;
    }
    armWatchdog();
    es.onopen = () => { backoff = MIN_BACKOFF_MS; onStatus(true); armWatchdog(); };
    es.onerror = () => scheduleReconnect();
    es.onmessage = (e) => { armWatchdog(); if (handlers.message) handlers.message(parse(e.data), e); };
    // The backend's liveness ping — resets the watchdog, never reaches a handler.
    es.addEventListener("ping", () => armWatchdog());
    Object.keys(handlers).forEach((name) => {
      if (name === "message") return;
      es.addEventListener(name, (e) => {
        armWatchdog();
        try { handlers[name](parse(e.data), e); } catch {}
      });
    });
  };

  connect();

  return {
    close() {
      closed = true;
      clearTimers();
      try { es && es.close(); } catch {}
      es = null;
    },
  };
}

export default openSSE;
